import type { ReactElement } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { GenreBody, gamesGenreSeo, type GameSort } from '@fias/public-site-ui';
import { ApiError } from '@/lib/api';
import { GAME_GENRE_OPTIONS } from '@/lib/game-genres';
import { siteOrigin, usePageHead } from '@/lib/document-head';
import { flattenGenrePages, useGameGenre } from '../hooks/useGamesCatalog';
import { GamesLoadFailed, GamesLoading } from './GamesStatus';
import { GamesNotFoundPage } from './GameDetailPage';

const SORTS: readonly GameSort[] = ['popular', 'newest', 'name'];

function sortFrom(value: string | null): GameSort {
  return SORTS.includes(value as GameSort) ? (value as GameSort) : 'popular';
}

/** One genre's games: `/a/games/genre/<id>[?sort=newest|name]`. */
export function GameGenrePage(): ReactElement {
  const { genreId = '' } = useParams();
  const [params] = useSearchParams();
  const known = GAME_GENRE_OPTIONS.some((g) => g.id === genreId);
  if (!known) return <GamesNotFoundPage />;
  return <KnownGenre genreId={genreId} sort={sortFrom(params.get('sort'))} />;
}

function KnownGenre({ genreId, sort }: { genreId: string; sort: GameSort }): ReactElement {
  const genre = useGameGenre(genreId, sort);
  const data = genre.data ? flattenGenrePages(genre.data.pages) : null;
  const seo = data ? gamesGenreSeo(data, siteOrigin() ?? '') : null;
  usePageHead(
    seo
      ? {
          title: seo.title,
          description: seo.description,
          canonicalPath: seo.canonicalPath,
          jsonLd: siteOrigin() ? seo.jsonLd : [],
          // Another order of the same games is not another page.
          noindex: sort !== 'popular' || undefined,
        }
      : null,
  );

  if (genre.error instanceof ApiError && genre.error.status === 404) {
    return <GamesNotFoundPage />;
  }
  if (genre.isError && !data) {
    return <GamesLoadFailed what="This genre" onRetry={() => void genre.refetch()} />;
  }
  if (!data) return <GamesLoading />;
  return (
    <GenreBody
      data={data}
      sort={sort}
      onLoadMore={genre.hasNextPage ? () => void genre.fetchNextPage() : undefined}
      loadingMore={genre.isFetchingNextPage}
    />
  );
}
